import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import type { ItineraryDay, MealSlot } from '../../../types';
import { SLOT_META } from './mealSlots';
import { dayKey } from './openDay';
import { useDateLocale } from '../../../utils/dateLocale';
import { ChevronDown, Icon } from '../../ui/Icon';

interface Props {
  day: ItineraryDay;
  coreSlots: MealSlot[];
  filledSlots: ReadonlySet<MealSlot>;
  open: boolean;
  isToday: boolean;
  onToggle: () => void;
}

/**
 * The part of a day that stays when it is folded. The slot icons are the
 * "day 4 has no dinner" signal of the open card, answered without opening it.
 */
export default function MealDayHeader({ day, coreSlots, filledSlots, open, isToday, onToggle }: Props) {
  const { t } = useTranslation();
  const locale = useDateLocale();
  const date = parseISO(dayKey(day));
  const complete = filledSlots.size === coreSlots.length;

  return (
    <button
      onClick={onToggle}
      aria-expanded={open}
      className="w-full flex items-center gap-3 px-4 sm:px-5 py-3.5 text-left cursor-pointer hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors"
    >
      <div
        className={`shrink-0 w-11 h-11 rounded-xl flex flex-col items-center justify-center leading-none ${
          isToday
            ? 'bg-teal-600 text-white dark:bg-teal-500'
            : 'bg-slate-50 text-slate-700 dark:bg-slate-800 dark:text-slate-200'
        }`}
      >
        <span className={`text-[9px] font-mono uppercase tracking-wider ${isToday ? 'text-teal-100' : 'text-slate-400'}`}>
          {format(date, 'MMM', { locale }).replace('.', '')}
        </span>
        <span className="text-base font-semibold tabular-nums mt-0.5">{format(date, 'd')}</span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] font-medium tracking-[0.14em] uppercase text-slate-400 dark:text-slate-500">
            {t('itinerary.dayLabel', { n: day.dayNumber })}
          </span>
          {isToday && (
            <span className="px-1.5 py-px rounded-full text-[10px] font-semibold bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300">
              {t('common.today')}
            </span>
          )}
        </div>
        <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
          <span className="capitalize">{format(date, 'EEEE', { locale })}</span>
          {day.title && <span className="text-slate-500 dark:text-slate-400 font-normal"> · {day.title}</span>}
        </p>
      </div>

      <div className="hidden sm:flex shrink-0 items-center gap-1" aria-hidden>
        {coreSlots.map(slot => {
          const meta = SLOT_META[slot];
          const filled = filledSlots.has(slot);
          return (
            <span
              key={slot}
              className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                filled ? meta.bg : 'border border-dashed border-slate-200 dark:border-slate-700'
              }`}
            >
              <Icon as={meta.Icon} size={12} className={filled ? meta.color : 'text-slate-300 dark:text-slate-600'} />
            </span>
          );
        })}
      </div>

      <span
        className={`shrink-0 font-mono text-[11px] tabular-nums px-1.5 py-0.5 rounded-md ${
          complete
            ? 'text-green-700 bg-green-50 dark:text-green-400 dark:bg-green-900/25'
            : 'text-slate-400 bg-slate-50 dark:text-slate-500 dark:bg-slate-800/70'
        }`}
      >
        {filledSlots.size}/{coreSlots.length}
      </span>

      <Icon
        as={ChevronDown}
        size={16}
        className={`shrink-0 text-slate-300 dark:text-slate-600 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
      />
    </button>
  );
}
